// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import {FormattedMessage} from 'react-intl';

import {ModalDialog, ModalSize} from '@time-webkit/all/organisms/modal-dialog';
import {Alert} from '@time-webkit/all/atoms/alert';
import {LockIcon} from '@time-webkit/all/icons/lock';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {type Channel} from 'mattermost-redux/types/channels';
import {localizeMessage} from 'utils/utils';

import styles from './styles.module.css';

export type Props = {
    channel: {
        id: Channel['id'];
        display_name: string;
    };
    onExited: () => void;
    actions: {
        leaveChannel: (channelId: string) => Promise<{data: boolean}>;
    };
}

export const LeavePrivateChannelModal = (props: Props) => {
    const handleSubmit = () => {
        const {actions, channel} = props;

        if (channel) {
            const channelId = channel.id;
            actions.leaveChannel(channelId).then((result: {data: boolean}) => {
                if (result.data) {
                    props.onExited();
                }
            });
        }
    };
    const title = (
        <div className={styles.title}>
            <FormattedMessage
                id='leave_private_channel_modal.title'
                defaultMessage='Leave {channel}?'
                ignoreTag={true}
                values={{
                    channel: <><LockIcon className={styles.lockIcon} width={16} height={16} />{props.channel.display_name}</>,
                }}
            />
        </div>
    );
    const description = localizeMessage(
        'leave_private_channel_modal.description',
        'If you leave the channel you will lose your saved messages, your reminders and scheduled messages will be deleted.',
    );
    const alert = localizeMessage(
        'leave_private_channel_modal.alert',
        'You must be re-invited in order to re-join this channel in the future.',
    );
    const confirmText = localizeMessage('leave_private_channel_modal.leave', 'Leave channel');
    const cancelText = localizeMessage('leave_private_channel_modal.stay', 'Stay');
    const typoCls = useTypography({
        size: TypographySize.BodyL,
    });

    return (
        <ModalDialog
            title={title}
            isOpen={true}
            confirmText={confirmText}
            cancelText={cancelText}
            size={ModalSize.SMALL}
            autoFocusCancelBtn={false}
            onClose={props.onExited}
            onConfirm={handleSubmit}
            onExited={props.onExited}
            isConfirmDestructive={true}
            nodeRef={{} as React.MutableRefObject<HTMLElement>}
        >
            <p className={typoCls}>{description}</p>
            <Alert message={alert} />
        </ModalDialog>
    );
};
